import { Fragment } from 'react';
import { Img } from './Img';
import { ProductTile } from './ProductTile';

/**
 * Foto ispirazionale intercalata nel listing prodotti (ASSETS A-13): un blocco
 * a tutta larghezza tra le righe di prodotti, orizzontale 16:9. Il sito non
 * vende: il blocco racconta l'ambiente in cui l'oggetto vive, non il prodotto
 * su fondo neutro. Le foto sono gli scatti reali in /assets/ispirazioni/.
 *
 * Il blocco occupa la riga intera con `col-span-full`, quindi va usato SOLO
 * dentro una griglia e su una foto orizzontale.
 */
export const InspirationBlock = ({ src, alt = '', testid }) => {
  if (!src) return null;
  return (
    <div data-testid={testid} className="col-span-full">
      <Img
        src={src}
        alt={alt}
        sizes="100vw"
        width={1600}
        aspectRatio={16 / 9}
        onError={(e) => {
          e.currentTarget.style.display = 'none';
        }}
        className="h-auto w-full object-cover"
      />
    </div>
  );
};

/**
 * Intercala le foto ispirazionali nella lista dei prodotti.
 * Dopo il dodicesimo elemento e in coda a una pagina (24 prodotti) compare un
 * blocco a tutta larghezza — «in mezzo ai prodotti metti altre due immagini»
 * (E.T., 15:47). 12 e 24 sono divisibili per 2, 3 e 4: qualunque sia il
 * numero di colonne attivo (mobile/tablet/desktop) il blocco cade sempre a
 * fine riga, senza lasciare celle vuote. Se un path manca, il blocco non si
 * rende.
 */
export const withInspiration = (items, srcs = [], alt = '') => {
  const [first, second] = srcs;
  const righe = items.map((p, i) => (
    <Fragment key={p.sku}>
      {i === 12 && <InspirationBlock src={first} alt={alt} testid="inspiration-1" />}
      <ProductTile product={p} />
    </Fragment>
  ));
  // Solo su pagina piena (24): su una coda parziale la posizione non sarebbe
  // per forza multipla del numero di colonne, e si rientrerebbe nel bug che
  // si sta correggendo. Assenza accettabile, come per i mondi senza foto.
  if (second && items.length === 24) {
    righe.push(<InspirationBlock key="inspiration-2" src={second} alt={alt} testid="inspiration-2" />);
  }
  return righe;
};

/**
 * Foto ispirazionali per mondo (ASSETS A-13): due scatti ambientati orizzontali
 * per ciascuno degli 8 mondi, dall'archivio SCENOGRAFICHE NEWSLETTER.
 * Mondo senza voce = listing senza blocchi (mancanza accettabile, non
 * regressione — ASSETS.md §5.2).
 */
export const INSPIRATION = {
  'per-la-tavola': [
    '/assets/ispirazioni/per-la-tavola-1.webp',
    '/assets/ispirazioni/per-la-tavola-2.webp',
  ],
  'per-la-cottura': [
    '/assets/ispirazioni/per-la-cottura-1.webp',
    '/assets/ispirazioni/per-la-cottura-2.webp',
  ],
  'per-gli-ambienti': [
    '/assets/ispirazioni/per-gli-ambienti-1.webp',
    '/assets/ispirazioni/per-gli-ambienti-2.webp',
  ],
  'per-il-bagno': [
    '/assets/ispirazioni/per-il-bagno-1.webp',
    '/assets/ispirazioni/per-il-bagno-2.webp',
  ],
  'per-le-feste': [
    '/assets/ispirazioni/per-le-feste-1.webp',
    '/assets/ispirazioni/per-le-feste-2.webp',
  ],
  'per-il-tempo-libero': [
    '/assets/ispirazioni/per-il-tempo-libero-1.webp',
    '/assets/ispirazioni/per-il-tempo-libero-2.webp',
  ],
  'per-laria-aperta': [
    '/assets/ispirazioni/per-laria-aperta-1.webp',
    '/assets/ispirazioni/per-laria-aperta-2.webp',
  ],
  'per-la-dispensa': [
    '/assets/ispirazioni/per-la-dispensa-1.webp',
    '/assets/ispirazioni/per-la-dispensa-2.webp',
  ],
};
